import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as api from '../api/api';

const AdminLayout = ({ children, title }) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = async () => {
        try {
            await api.logout();
        } catch (e) {
            // Ignore error since we are logging out locally anyway
        }
        localStorage.removeItem('userInfo');
        navigate('/admin/login');
    };

    const isActive = (path) => location.pathname.startsWith(path);

    const menuItems = [
        { path: '/admin/dashboard', icon: 'fas fa-th-large', label: 'Dashboard' },
        { path: '/admin/projects', icon: 'fas fa-briefcase', label: 'Projects' },
        { path: '/admin/sections', icon: 'fas fa-layer-group', label: 'Sections' },
        { path: '/admin/resume', icon: 'fas fa-file-alt', label: 'Resume' },
        { path: '/admin/skills', icon: 'fas fa-tools', label: 'Skills' },
        { path: '/admin/hero', icon: 'fas fa-magic', label: 'Hero Page' },
        { path: '/admin/services', icon: 'fas fa-concierge-bell', label: 'Services' },
    ];

    return (
        <div className="min-h-screen bg-gray-900 font-sans text-gray-100 flex flex-col" id="admin-root">
            {/* Horizontal Top Navbar */}
            <header className="bg-gray-800 border-b border-gray-700 shadow-lg sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo */}
                        <div className="flex items-center">
                            <h1 className="text-xl font-bold tracking-wider text-white">
                                ADMIN<span className="text-indigo-500">PANEL</span>
                            </h1>
                        </div>

                        {/* Desktop Menu */}
                        <nav className="hidden md:flex space-x-4">
                            {menuItems.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${isActive(item.path)
                                        ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.4)]'
                                        : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                                        }`}
                                >
                                    <i className={`${item.icon} mr-2`}></i>
                                    {item.label}
                                </Link>
                            ))}
                        </nav>

                        {/* Right Section: Logout & Mobile Toggle */}
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={handleLogout}
                                className="hidden md:flex items-center px-4 py-2 text-sm font-medium text-red-400 border border-red-400/30 rounded-lg hover:bg-red-500 hover:text-white transition-all"
                            >
                                <i className="fas fa-sign-out-alt mr-2"></i>
                                Logout
                            </button>

                            {/* Mobile menu button */}
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="md:hidden p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none"
                            >
                                <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Menu */}
                <div className={`${mobileMenuOpen ? 'block' : 'hidden'} md:hidden bg-gray-800 border-t border-gray-700`}>
                    <div className="px-2 pt-2 pb-3 space-y-1">
                        {menuItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => setMobileMenuOpen(false)}
                                className={`flex items-center px-4 py-3 text-base font-medium rounded-md ${isActive(item.path)
                                    ? 'bg-indigo-600 text-white'
                                    : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                                    }`}
                            >
                                <i className={`${item.icon} w-6 mr-2 text-center`}></i>
                                {item.label}
                            </Link>
                        ))}
                        <button
                            onClick={handleLogout}
                            className="flex items-center w-full px-4 py-3 text-base font-medium text-red-400 hover:bg-gray-700"
                        >
                            <i className="fas fa-sign-out-alt w-6 mr-2 text-center"></i>
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            {/* Title Bar (Optional, provides context for each page) */}
            <div className="bg-gray-800/50 border-b border-gray-700 py-4 px-4 sm:px-6 lg:px-8 shadow-sm">
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-lg font-semibold text-white truncate flex items-center">
                        <i className="fas fa-chevron-right text-indigo-500 text-xs mr-3"></i>
                        {title}
                    </h2>
                </div>
            </div>

            {/* Main Content Area */}
            <main className="flex-1 overflow-y-auto p-4 md:p-8">
                <div className="max-w-7xl mx-auto">
                    {children}
                </div>
            </main>

            {/* Simple Footer */}
            <footer className="bg-gray-800/50 border-t border-gray-700 py-4 px-4 text-center text-xs text-gray-500">
                &copy; {new Date().getFullYear()} Admin Dashboard. All rights reserved.
            </footer>
        </div>
    );
};

export default AdminLayout;
